import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import { ArrowLeft, Users, ShieldCheck, CheckCircle2, XCircle, Loader2, ArrowRight } from "lucide-react";
import { GFFrame } from "@/components/GFFrame";
import { api } from "@/lib/gfApi";

const PURPLE = "#4B006E";
const GOLD = "#D4AF37";

export default function CohortEntry() {
  const navigate = useNavigate();
  const [code, setCode] = useState("");
  const [loading, setLoading] = useState(false);
  const [result, setResult] = useState(null); // {valid, cohort} | {valid:false,message}

  const validate = async () => {
    if (!code.trim()) return;
    setLoading(true);
    setResult(null);
    try {
      const r = await api.post("/cohort/validate", { code }).then((x) => x.data);
      setResult(r);
      if (r.valid) sessionStorage.setItem("gf_cohort", JSON.stringify(r.cohort));
    } catch (e) {
      setResult({ valid: false, message: "Ralat pengesahan. Sila cuba lagi." });
    } finally {
      setLoading(false);
    }
  };

  return (
    <GFFrame testId="cohort-entry-page">
      <div className="flex min-h-screen flex-col" style={{ background: PURPLE }}>
        <div className="px-6 pt-8">
          <button onClick={() => navigate("/")} data-testid="cohort-back-btn"
            className="flex w-fit items-center gap-1 text-sm" style={{ color: "#C9B2D6" }}>
            <ArrowLeft className="h-4 w-4" /> Kembali
          </button>
        </div>

        <div className="flex flex-1 flex-col px-6 pb-10 pt-6">
          <motion.div initial={{ opacity: 0, y: 16 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.5 }}>
            <span className="flex h-12 w-12 items-center justify-center rounded-2xl" style={{ background: "rgba(255,255,255,0.12)" }}>
              <Users className="h-6 w-6" style={{ color: GOLD }} />
            </span>
            <p className="mt-5 text-xs font-bold uppercase tracking-[0.3em]" style={{ color: GOLD }}>Organisation / Cohort</p>
            <h1 className="mt-2 font-heading text-4xl font-bold leading-tight text-white">
              Fahami pasukan anda.<br />Kukuhkan organisasi anda.
            </h1>
            <p className="mt-3 text-sm leading-relaxed" style={{ color: "#C9B2D6" }}>
              Penilaian cohort berasaskan data untuk memahami kekuatan, cabaran dan kesediaan pasukan
              sebelum dan selepas program.
            </p>
          </motion.div>

          {(!result || !result.valid) && (
            <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.5, delay: 0.1 }} className="mt-10">
              <label className="text-sm font-semibold" style={{ color: "#E5D4EE" }}>GF Access Code</label>
              <input
                data-testid="cohort-code-input"
                value={code}
                onChange={(e) => setCode(e.target.value.toUpperCase())}
                onKeyDown={(e) => e.key === "Enter" && validate()}
                placeholder="cth: JTDC-TD26-A01"
                className="mt-2 w-full rounded-2xl border-2 bg-white/5 px-4 py-3.5 text-center text-lg font-bold uppercase tracking-widest text-white outline-none transition-colors placeholder:text-white/30"
                style={{ borderColor: "rgba(240,199,80,0.4)" }}
              />
              {result && !result.valid && (
                <div data-testid="cohort-error" className="mt-3 flex items-start gap-2 rounded-xl bg-white/10 p-3">
                  <XCircle className="mt-0.5 h-4 w-4 shrink-0 text-rose-300" />
                  <p className="text-sm text-rose-100">{result.message}</p>
                </div>
              )}
              <button
                data-testid="cohort-validate-btn"
                onClick={validate}
                disabled={loading || !code.trim()}
                className="mt-5 flex w-full items-center justify-center gap-3 rounded-full py-4 text-base font-bold transition-transform duration-200 active:scale-[0.98] disabled:opacity-50"
                style={{ background: GOLD, color: PURPLE }}
              >
                {loading ? (<><Loader2 className="h-5 w-5 animate-spin" /> Mengesahkan…</>) : "MASUK DENGAN GF ACCESS CODE"}
              </button>
              <button
                data-testid="cohort-after-link"
                onClick={() => navigate("/cohort/after")}
                className="mt-4 w-full text-center text-sm font-semibold underline-offset-4 hover:underline"
                style={{ color: "#C9B2D6" }}
              >
                Sudah lengkapkan Before? Teruskan ke After Assessment →
              </button>
            </motion.div>
          )}

          {result && result.valid && (
            <motion.div data-testid="cohort-valid" initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.5 }} className="mt-8">
              <div className="rounded-2xl bg-white/10 p-5">
                <div className="flex items-center gap-2">
                  <CheckCircle2 className="h-5 w-5 text-emerald-300" />
                  <span className="text-sm font-bold text-emerald-200">Kod disahkan</span>
                </div>
                <div className="mt-4 space-y-2 text-sm">
                  <Row k="Organisasi" v={result.cohort.organisation} />
                  <Row k="Program" v={result.cohort.programme} />
                  <Row k="Kohort" v={result.cohort.cohort_name} />
                  <Row k="Kod Akses" v={result.cohort.access_code} gold />
                  <Row k="Peserta" v={`${result.cohort.registered} / ${result.cohort.seats} tempat`} />
                </div>
              </div>
              <div className="mt-4 flex items-start gap-2 rounded-xl bg-white/5 p-3">
                <ShieldCheck className="mt-0.5 h-4 w-4 shrink-0" style={{ color: GOLD }} />
                <p className="text-xs leading-relaxed" style={{ color: "#C9B2D6" }}>
                  Perkongsian anda dikendalikan secara sulit. Organisasi hanya menerima dapatan
                  keseluruhan (aggregated), bukan jawapan individu.
                </p>
              </div>
              <button
                data-testid="cohort-continue-btn"
                onClick={() => navigate("/cohort/mula")}
                className="mt-5 flex w-full items-center justify-center gap-3 rounded-full py-4 text-base font-bold transition-transform duration-200 active:scale-[0.98]"
                style={{ background: GOLD, color: PURPLE }}
              >
                SETERUSNYA <ArrowRight className="h-5 w-5" />
              </button>
            </motion.div>
          )}
        </div>
      </div>
    </GFFrame>
  );
}

const Row = ({ k, v, gold }) => (
  <div className="flex items-center justify-between">
    <span style={{ color: "#9E7EB0" }}>{k}</span>
    <span className="font-semibold" style={{ color: gold ? "#F0C750" : "#fff" }}>{v}</span>
  </div>
);
